import { useRef, useState } from "react";
import { DOLGU_ALANLARI, PQR_ALANLARI, WPS_ALANLARI, WPS_FARK_ETIKETI, hedefEsit, wpsBelgesiEtiketi, type GelenHedef, type WpsBelgesiKarti, type WpsSatiri } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  WPS_UCU,
  okumaDugmesi,
  okumaEylemleri,
  wpsEskiSurumMetni,
  wpsFarkSonucMetni,
  wpsFarkTonu,
  wpsFormu,
  wpsKarsilastirmaSatirlari,
  wpsNedenMetni,
  wpsYenidenMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarih, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 3 · PR 18: Documents kartının WPS, PQR ve dolgu onay sayfası "Document reading" bölümü (prototip belgeler--wps-okuma, kart 1-6; `SarfOkumaBolumu` iskeleti). Okuma
 * yalnız eşlemenin, kuralların, WPS'in dolgu listesinin ve "Use the reading"in kullandığı değerleri gösterir (sözleşme §11): WPS'te 10 değer ile dolgu ve dayanak PQR satırları,
 * PQR'da 2, dolgu onayında 1 değer ve dolgu satırları. OASIS WPS'i Registers'ta birebir (PQR'ı onu anan okunmuş WPS'ten) önerir; "Use this place" kartın bugünkü tür+yer kaydını
 * çağırır, "Use the reading" yalnız dosyalama formunun Doc no.'sunu kesin değerle doldurur (sunucuya yazmaz). "Differences from Registers" yalnız gösterilir: Registers'taki WPS
 * okumayla değişmez. Dosyalanmış WPS ya da PQR satırında aynı WPS'in eski belgeleri listelenir; PM ya da sistem yöneticisi her birini tek tek "Mark as superseded" ile işaretler
 * (Registers'taki aynı işaret). `salt` kapanmış satır içindir: form ve karar düğmeleri yok. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";
const SATIR = "grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]";

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");
const satirDegeri = (s: WpsSatiri, alan: string) => s.degerler.find((d) => d.alan === alan);

export interface WpsFormYardimi {
  /** "Use the reading": dosyalama formunun Doc no.'su (yalnız kesin değer) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function WpsOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: WpsBelgesiKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: WpsFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;
  const y = k.yer;
  const cins = k.cins;
  const kod = y?.yer?.tur === "kutuk" && y.yer.kutuk === "wps" ? y.yer.anahtar : null;
  const noAlani = cins === "pqr" ? "pqr_no" : "wps_no";
  const okunanNo = k.degerler.find((d) => d.alan === noAlani)?.deger ?? null;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = wpsKarsilastirmaSatirlari(k);
  const yeniden = wpsYenidenMetni(k.yenidenKarsilastirma, kod);
  const toplamUyari = k.karsilastirma?.uyarilar.length ?? 0;
  const alanlar = cins === "wps" ? WPS_ALANLARI : cins === "pqr" ? PQR_ALANLARI : DOLGU_ALANLARI;
  const kaynakMetni = (s: { bas: number; son: number }) =>
    cins === "wps" ? cevir("WPS p.{0}", aralik(s)) : cins === "pqr" ? cevir("PQR p.{0}", aralik(s)) : cevir("Filler approval p.{0}", aralik(s));
  const eskiIsaretle = (x: { kutukBelgeId: string; ad: string }) => {
    const onay = [
      cevir("Mark “{0}” as superseded?", x.ad),
      cevir("It stays on this record under Superseded documents with its PDF. OASIS no longer uses it as a current document: Riser Documentation does not list it; alerts and the NDT signer check do not count it."),
    ].join("\n");
    if (!window.confirm(onay)) return;
    void calistir(`eski-${x.kutukBelgeId}`, () => api.post(WPS_UCU.eskiSurum(satirId), { kutukBelgeId: x.kutukBelgeId }));
  };

  return (
    <div data-bolum="wps-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the WPS document for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[o.sayfalar ? kaynakMetni(o.sayfalar) : null, k.kod ? cevir("Reading job {0}", k.kod) : null].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm" data-bolum="bl-parca">
            <li className={SATIR}>
              <span>
                <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                {o.sayfalar
                  ? <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: o.sayfalar.bas })} target="_blank" rel="noreferrer">p.{aralik(o.sayfalar)}</a>
                  : <span className="text-xs">—</span>}
              </span>
              <span className="min-w-0 [overflow-wrap:anywhere]">
                <span className="block text-xs text-ink-3">{cins === "pqr" ? cevir("PQR number") : cevir("WPS number")}</span>
                <span className="block font-medium">{okunanNo ?? "—"}</span>
              </span>
              <span className="col-span-2 min-w-0 [overflow-wrap:anywhere] @min-[40rem]:col-span-1">
                <span className="block text-xs text-ink-3">{cevir("Place")}</span>
                {kod && y && y.durum !== "yok" && <span className="mr-1.5">{y.yerMetni ?? kod}</span>}
                {y && y.durum !== "yok"
                  ? <Etiket tone={durumTonu(y.durum)}>{y.durum === "kisi" ? cevir("this place") : cevir("proposed")}</Etiket>
                  : <span className="text-ink-2">{y ? wpsNedenMetni(y.neden, y.deger, cins, cins === "pqr" ? okunanNo : null) : "—"}</span>}
                {y?.durum === "oneri" && <span className="mt-0.5 block text-xs text-ink-3">{wpsNedenMetni(null, null, cins, null, kod)}</span>}
                {yardim && y?.durum === "oneri" && y.yer && !hedefEsit(y.yer, hedef) && (
                  <Buton kucuk className="mt-1" disabled={mesgul !== null} mesgul={mesgul === "yer"} onClick={() => calistir("yer", () => yardim.yerKaydet(y.yer!))}>{cevir("Use this place")}</Buton>
                )}
              </span>
            </li>
          </ul>

          {k.eskiSurumler && k.eskiSurumler.length > 0 && (
            <div data-bolum="eski-surumler">
              <AltBaslik>{cevir("Older versions on this WPS")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Document")}</th>
                    <th className={TABLO_BASI}>{cevir("Filed")}</th>
                    <th className={TABLO_BASI}>{cevir("Superseded")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.eskiSurumler.map((x) => (
                      <tr key={x.kutukBelgeId}>
                        <td className={TABLO_HUCRESI}>{x.ad}{x.belgeNo && <span className="block text-xs text-ink-3">{cevir("Doc no.")} {x.belgeNo}</span>}</td>
                        <td className={TABLO_HUCRESI}>{x.kim} · {tarih(x.ts.slice(0, 10))}</td>
                        <td className={TABLO_HUCRESI}>
                          {x.isaretli
                            ? wpsEskiSurumMetni(x)
                            : <Buton kucuk disabled={mesgul !== null || !k.eylemler.eskiSurum} mesgul={mesgul === `eski-${x.kutukBelgeId}`} onClick={() => eskiIsaretle(x)}>{cevir("Mark as superseded")}</Buton>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!k.eylemler.eskiSurum && k.eskiSurumler.some((x) => !x.isaretli) && <p className="mt-1 text-xs text-ink-2">{cevir("A PM or System administrator marks documents superseded.")}</p>}
              <p className="mt-1 text-xs text-ink-3">
                {cevir("“Mark as superseded” keeps the old document on the WPS under Superseded documents with its PDF; OASIS then no longer uses it as current (filler list, alerts, Riser Documentation). It can be marked current again on Registers. A PM or System administrator marks documents superseded.")}
              </p>
            </div>
          )}

          <AltBaslik>{cins === "wps" ? cevir("WPS") : cins === "pqr" ? cevir("PQR") : cevir("Alternative filler metal approval")}</AltBaslik>
          <ul className="space-y-0.5 text-sm">
            {alanlar.map((a) => {
              const d = k.degerler.find((x) => x.alan === a.alan);
              return (
                <li key={a.alan} className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[10rem] shrink-0 text-ink-2">{cevir(wpsBelgesiEtiketi(a.alan))}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {d?.deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                    {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                  </span>
                </li>
              );
            })}
            {cins !== "pqr" && (
              <li className="flex gap-2" data-bolum="dolgu-listesi">
                <Nokta k={k.dolgular.length ? "kesin" : "bos"} />
                <span className="w-[10rem] shrink-0 text-ink-2">{cevir("Filler list")}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {k.dolgular.length
                    ? k.dolgular.map((s) => (
                      <span key={s.sira} className="block">
                        {[satirDegeri(s, "sinif")?.deger, satirDegeri(s, "ticari_ad")?.deger].filter(Boolean).join(" · ") || "—"}
                        {satirDegeri(s, "sinif")?.kesinlik === "suphe" && <span className="ml-1 text-xs text-ink-3">({cevir("check")})</span>}
                        {sayfaLinki(satirDegeri(s, "sinif")?.sayfa ?? null)}
                      </span>
                    ))
                    : <span className="text-ink-3">{cevir("not read")}</span>}
                </span>
              </li>
            )}
            {cins === "wps" && (
              <li className="flex gap-2" data-bolum="dayanak-pqr">
                <Nokta k={k.pqrlar.length ? "kesin" : "bos"} />
                <span className="w-[10rem] shrink-0 text-ink-2">{cevir("Supporting PQR")}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {k.pqrlar.length
                    ? k.pqrlar.map((s) => <span key={s.sira} className="block">{satirDegeri(s, "pqr_no")?.deger ?? "—"}{sayfaLinki(satirDegeri(s, "pqr_no")?.sayfa ?? null)}</span>)
                    : <span className="text-ink-3">{cevir("not read")}</span>}
                </span>
              </li>
            )}
          </ul>

          {cins === "wps" && k.farklar.length > 0 && (
            <div data-bolum="farklar">
              <AltBaslik>{cevir("Differences from Registers")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Field")}</th>
                    <th className={TABLO_BASI}>{cevir("On the document")}</th>
                    <th className={TABLO_BASI}>{cevir("In Registers")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.farklar.map((f) => (
                      <tr key={f.alan} data-alan={f.alan}>
                        <td className={TABLO_HUCRESI}>{cevir(WPS_FARK_ETIKETI[f.alan])}</td>
                        <td className={TABLO_HUCRESI}>{f.belge ?? "—"}{sayfaLinki(f.sayfa)}</td>
                        <td className={TABLO_HUCRESI}>{f.kayit ?? "—"}</td>
                        <td className={TABLO_HUCRESI}><Etiket tone={wpsFarkTonu(f.sonuc)}>{wpsFarkSonucMetni(f.sonuc)}</Etiket></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">{cevir("OASIS does not change the WPS in Registers from a reading. A PM or System administrator edits it on Registers.")}</p>
            </div>
          )}

          {yardim && cins !== "dolgu" && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null} onClick={() => yardim.kullan(wpsFormu(k))}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {cins === "wps"
                  ? cevir("“Use the reading” fills Doc no. with the certain WPS number of the document; an uncertain or empty value stays empty for you to type. Nothing is filed until you confirm.")
                  : cevir("“Use the reading” fills Doc no. with the certain PQR number of the document; an uncertain or empty value stays empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {cins === "wps"
              ? cevir("Not read: the date, the issuer, base materials and P-Numbers, thickness and diameter ranges, positions and backing, electrical values, gas and tungsten, and the signers. Not checked yet: the qualification range of welders (WPQ-03 diameter, thickness and position, waits for approval) and known misprints (WPS-07, waits for the list).")
              : cins === "pqr"
                ? cevir("Not read: the date, the issuer, the coupon, test results, MDMT and the signers.")
                : cevir("Not read: the date and the signatures.")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(karsilastirmaSatirlari.length > 0 || yeniden) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
              {yeniden && (
                <li className="flex gap-2">
                  <Nokta k="kesin" />
                  <span className="min-w-0">{yeniden}</span>
                </li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <>
                  <ul className="space-y-1.5">
                    {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                  </ul>
                  {k.karsilastirma.uyarilar.some((u) => u.kod === "WPS-01" || u.kod === "WPS-03") && (
                    <p className="mt-1 text-xs text-ink-3">
                      {k.karsilastirma.uyarilar.some((u) => u.kod === "WPS-01") && <span className="block">{cevir("A PM or System administrator changes the revision on Registers; the reading does not change it.")}</span>}
                      {cevir("The decision is a record of its own: the WPS in Registers does not change. A PM or System administrator edits it on Registers if the document is right.")}
                    </p>
                  )}
                </>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(WPS_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(WPS_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(WPS_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
